/**
 * Resolve what a subscription is allowed to do. Consumed by callables to gate
 * server actions and by the UI to gate tabs, always from the same source.
 */

import {
  PLAN_CATALOG,
  UNLIMITED,
  type BillingFeature,
  type PlanId,
} from './plans';
import {
  isEntitledStatus,
  isReadOnlyStatus,
  type SubscriptionStatus,
} from './subscriptionStatus';

/** Plan applied while the account is degraded for non-payment. */
export const DEGRADED_PLAN_ID: PlanId = 'basic';

export interface FeatureOverride {
  feature: BillingFeature;
  /** Epoch ms; null never expires. */
  expiresAtMs: number | null;
}

export interface EntitlementSource {
  planId: PlanId;
  status: SubscriptionStatus;
  /** God override; falls back to the plan quota when null. */
  houseQuota?: number | null;
  seatQuota?: number | null;
  overrides?: readonly FeatureOverride[] | null;
  /**
   * God exempted this banker from billing: full access, no invoices, no dunning.
   * Independent from the plan so revoking it restores the stored plan as it was.
   */
  exempt?: boolean;
}

/** Plan whose features an exempt banker gets: everything. */
export const EXEMPT_PLAN_ID: PlanId = 'platinum';

export interface Entitlements {
  planId: PlanId;
  /** Plan actually applied, which drops to basic while degraded. */
  effectivePlanId: PlanId;
  status: SubscriptionStatus;
  active: boolean;
  readOnly: boolean;
  /** Billing does not apply to this banker at all. */
  exempt: boolean;
  houseQuota: number;
  seatQuota: number;
  auditRetentionDays: number;
  features: readonly BillingFeature[];
}

function activeOverrides(
  overrides: readonly FeatureOverride[] | null | undefined,
  nowMs: number,
): BillingFeature[] {
  if (!overrides?.length) return [];
  return overrides
    .filter((o) => o.expiresAtMs == null || o.expiresAtMs > nowMs)
    .map((o) => o.feature);
}

function resolveQuota(override: number | null | undefined, planQuota: number): number {
  if (override == null) return planQuota;
  if (override === UNLIMITED) return UNLIMITED;
  return Math.max(0, Math.trunc(override));
}

/**
 * Exemption ignores plan and status: whatever the row says, the banker operates
 * with everything and is never gated or degraded.
 */
function exemptEntitlements(source: EntitlementSource): Entitlements {
  const plan = PLAN_CATALOG[EXEMPT_PLAN_ID];
  return {
    planId: source.planId,
    effectivePlanId: EXEMPT_PLAN_ID,
    status: source.status,
    active: true,
    readOnly: false,
    exempt: true,
    houseQuota: UNLIMITED,
    seatQuota: UNLIMITED,
    auditRetentionDays: plan.auditRetentionDays,
    features: plan.features,
  };
}

export function resolveEntitlements(
  source: EntitlementSource,
  nowMs: number = Date.now(),
): Entitlements {
  if (source.exempt === true) return exemptEntitlements(source);

  const readOnly = isReadOnlyStatus(source.status);
  const degraded = source.status === 'degraded';
  const effectivePlanId = degraded ? DEGRADED_PLAN_ID : source.planId;
  const plan = PLAN_CATALOG[effectivePlanId];
  const contracted = PLAN_CATALOG[source.planId];

  const granted = readOnly
    ? []
    : [...new Set([...plan.features, ...activeOverrides(source.overrides, nowMs)])];

  return {
    planId: source.planId,
    effectivePlanId,
    status: source.status,
    active: isEntitledStatus(source.status),
    readOnly,
    exempt: false,
    // Quotas follow the contracted plan: degrading must not orphan existing houses.
    houseQuota: resolveQuota(source.houseQuota, contracted.houseQuota),
    seatQuota: resolveQuota(source.seatQuota, contracted.seatQuota),
    auditRetentionDays: plan.auditRetentionDays,
    features: granted,
  };
}

export function hasEntitlement(ent: Entitlements, feature: BillingFeature): boolean {
  if (ent.readOnly) return false;
  return ent.features.includes(feature);
}

export function isWithinQuota(quota: number, count: number): boolean {
  if (quota === UNLIMITED) return true;
  return count <= quota;
}

export function isOverHouseQuota(ent: Entitlements, houseIds: readonly string[]): boolean {
  return !isWithinQuota(ent.houseQuota, houseIds.length);
}

/**
 * Houses that would not fit under a quota, oldest kept first. Used to explain a
 * blocked downgrade instead of silently breaking access afterwards.
 */
export function housesOverQuota(quota: number, houseIds: readonly string[]): string[] {
  if (quota === UNLIMITED || houseIds.length <= quota) return [];
  return houseIds.slice(Math.max(0, quota));
}

export function isOverSeatQuota(ent: Entitlements, seatCount: number): boolean {
  return !isWithinQuota(ent.seatQuota, seatCount);
}

/** Quota check for a target plan, used before accepting a plan change. */
export function planFitsUsage(
  planId: PlanId,
  houseIds: readonly string[],
  seatCount: number,
): { ok: boolean; houses: string[]; seatsOver: number } {
  const plan = PLAN_CATALOG[planId];
  const houses = housesOverQuota(plan.houseQuota, houseIds);
  const seatsOver = plan.seatQuota === UNLIMITED
    ? 0
    : Math.max(0, seatCount - plan.seatQuota);
  return { ok: houses.length === 0 && seatsOver === 0, houses, seatsOver };
}
